import * as mapboxgl from 'mapbox-gl';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  addLayersToMap,
  CommentsEditEngine, GeoCoords,
  MapboxSpecificService, ObeliskComment, Pathway,
  PathwayEditEngine,
  zoneToFeatureLineString,
  QGraphVertexWithCircle, coordsToQGraph
} from 'quest-atlas-angular-components';
import { BehaviorSubject, of } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslocoService } from '@ngneat/transloco';
import { v4 as uuidv4 } from 'uuid';
import * as turf from '@turf/turf';
import { coordsToGpx } from 'quest-atlas-angular-components';
import { signal } from '@angular/core';

export abstract class MapEditorBase {
  readonly MAX_NUMBER_OF_IMAGES = 11;

  map!: mapboxgl.Map;
  mapLoaded = false;

  currentPathway?: FormGroup;
  pathwaysEditEngines = new Map<string, PathwayEditEngine>();
  pathwaysStrokeColors = new Map<string, BehaviorSubject<string>>

  currentComment?: FormGroup;
  commentsEditEngines = new Map<string, CommentsEditEngine>();
  commentsShown = true;

  userGeo?: GeoCoords;

  selectedPathwaysForMerge = signal<FormGroup[]>([]);
  showMergeModal = signal(false);
  showSplitModal = signal(false);
  showCutModal = signal(false);
  pathwayForOperation = signal<FormGroup | null>(null);

  // For split operation
  splitVertexIndex = signal<number>(0);
  splitRunningMarker?: mapboxgl.Marker;

  // For cut operations
  cutStartVertexIndex = signal<number>(0);
  cutEndVertexIndex = signal<number>(0);
  cutMode = signal<'start' | 'end' | 'section' | null>(null);
  cutRunningMarkers: mapboxgl.Marker[] = [];

  protected constructor(
    protected mapboxSpecificService: MapboxSpecificService,
    protected snackBar: MatSnackBar,
    protected transloco: TranslocoService,
    protected fb: FormBuilder
  ) {}

  addPathway(): void {
    this.stopEditCurrentPathway();

    const pathway = this.createPathwayFormGroup({
      uuid: uuidv4(),
      name: '',
      color: '#ffa822',
      vertices: [],
      mappedPathProfile: 'walking',
      mappedToRoads: false  // No profile selected by default
    });
    this.currentPathway = pathway;
    this.getPathways().push(pathway);

    const pE = this.initPathway(pathway);

    setTimeout(() => {
      pathway.markAsTouched();
      pathway.markAsDirty();
      pathway.markAllAsTouched();
    }, 100);

    this.map.once('styledata', () => {
      pE.startEditing();
    });
  }

  importFromGPX(): void {
  //   import .gpx file
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.gpx,application/gpx+xml';
    input.onchange = (e: any) => {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onload = (event: any) => {
        try {
          const text = event.target.result;
          const coords = [];

          const gpx = new DOMParser().parseFromString(text, 'application/xml');
          const trkpts = gpx.getElementsByTagName('trkpt');
          for (let i = 0; i < trkpts.length; i++) {
            const lat = parseFloat(trkpts[i].getAttribute('lat')!);
            const lng = parseFloat(trkpts[i].getAttribute('lon')!);
            coords.push({lat, lng});
          }

          if (coords.length < 2) {
            throw new Error('Not enough coordinates in GPX file');
          }

          console.log('Imported GPX coords', coords);

          this.addPathway();

          this.map.once('styledata', () => {
            setTimeout(() => {
              const pE = this.pathwaysEditEngines.get(this.currentPathway.value.uuid);
              for (const c of coords) {
                pE.addVertex(c);
              }

              pE.updateZoneOutlineOnMap();
              pE.stopEditing();
            });
          });
        } catch (error) {
          console.log('Error parsing GPX file', error);
          this.snackBar.open(
            this.transloco.translate('invalidGpxFile'),
            this.transloco.translate('close'),
            {
              verticalPosition: 'bottom',
              duration: 5000,
              panelClass: 'error-snackbar'
            });
        }
      }
      reader.readAsText(file);
    };
    input.click();
  }

  initPathway(pathway: FormGroup, addLayers = true): PathwayEditEngine {
    this.pathwaysStrokeColors.set(pathway.value.uuid, new BehaviorSubject<string>(pathway.value.color));

    pathway.valueChanges.subscribe((value) => {
      this.pathwaysStrokeColors.get(pathway.value.uuid).next(value.color);
    });

    console.log('init pathway', pathway.value);

    const pE = new PathwayEditEngine(
      this.map,
      of({button: 'confirm'}),
      'pathway-' + pathway.value.uuid,
      this.pathwaysStrokeColors.get(pathway.value.uuid).asObservable(),
      pathway.value.path,
      () => {

      });

    this.pathwaysStrokeColors.get(pathway.value.uuid).subscribe((value) => {

    });

    pE.onEdit$().subscribe(() => {
      const data = pE.getData();
      pathway.patchValue({
        path: data.cleanZone,
        vertices: data.vertices,
      });

      if (pathway.value.mappedToRoads) {
        this.loadMapMatching(pathway);
      }
    });

    pE.stopEdit$().subscribe(() => {
      this.currentPathway = undefined;
      pE.hideMarkers();
    });

    this.pathwaysEditEngines.set(
      pathway.value.uuid,
      pE
    );

    if (pathway.value.mappedToRoads) {
      this.map.once('styledata', () => {
        this.updateMappedPathwaysLayerData(pathway, pathway.value.mapMappedPath);
      });
    }

    if (addLayers) {
      this.addPathwayMapLayers(pathway);
      this.pathwaysStrokeColorsUpdates(pathway);
    }

    return pE;
  }

  stopEditCurrentPathway(): void {
    if (this.currentPathway) {
      this.pathwaysEditEngines.get(this.currentPathway.value.uuid).hideMarkers();
      this.pathwaysEditEngines.get(this.currentPathway.value.uuid).stopEditing();
      this.currentPathway = undefined;
    }
  }

  startEditPathway(pathway: FormGroup): void {
    if (this.currentPathway === pathway) {
      this.stopEditCurrentPathway()
      return;
    }

    if (this.currentPathway) {
      this.stopEditCurrentPathway();
    }

    this.currentPathway = pathway;
    this.pathwaysEditEngines.get(pathway.value.uuid).startEditing();
    this.pathwaysEditEngines.get(pathway.value.uuid).revealMarkers();
  }

  removePathway(pathway: FormGroup): void {
    this.stopEditCurrentPathway();
    this.pathwaysEditEngines.get(pathway.value.uuid).destroy();
    this.pathwaysEditEngines.delete(pathway.value.uuid);

    this.pathwaysStrokeColors.get(pathway.value.uuid).complete();
    this.pathwaysStrokeColors.delete(pathway.value.uuid);

    this.getPathways().removeAt(this.getPathways().controls.indexOf(pathway));

    //   remove map sources
    this.map.removeLayer('pathway-' + pathway.value.uuid);
    this.map.removeLayer('pathway-' + pathway.value.uuid + '-outline');
    this.map.removeSource('pathway-' + pathway.value.uuid);

    this.map.removeLayer('pathway-' + pathway.value.uuid + '-mapped');
    this.map.removeLayer('pathway-' + pathway.value.uuid + '-mapped-outline');
    this.map.removeSource('pathway-' + pathway.value.uuid + '-mapped');
  }

  exportPathway(pathway: FormGroup): void {
    const gpxString = coordsToGpx(this.pathwaysEditEngines.get(pathway.value.uuid).getData().vertices, pathway.value.name || 'Track');

    const blob = new Blob([gpxString], { type: 'application/gpx+xml' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = (pathway.value.name || 'track') + '.gpx';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  changePathwayMappingProfile(pathway: FormGroup, profile: 'walking' | 'cycling' | 'driving'): void {
    // If clicking the already active profile, deactivate road mapping
    if (profile === pathway.value.mappedPathProfile && pathway.value.mappedToRoads) {
      pathway.patchValue({
        mappedToRoads: false,
        mapMappedPath: null
      });
      this.updatePathLayers(pathway);
      this.updateMappedPathwaysLayerData(pathway);
      return;
    }

    // Set the new profile
    pathway.patchValue({
      mappedPathProfile: profile,
      mappedToRoads: true  // Enable road mapping when selecting a profile
    });

    this.updatePathLayers(pathway);
    this.loadMapMatching(pathway);
  }

  mapToRoadsToggle(pathway: FormGroup): void {
    pathway.patchValue({
      mappedToRoads: !pathway.value.mappedToRoads
    });

    this.updatePathLayers(pathway);

    if (pathway.value.mappedToRoads) {
      this.loadMapMatching(pathway);
    } else {
      pathway.patchValue({
        mapMappedPath: null
      });

      this.updateMappedPathwaysLayerData(pathway);
    }
  }

  removeComment(comment: FormGroup): void {
    this.stopEditCurrentComment();
    this.commentsEditEngines.get(comment.value.uuid).destroy();
    this.commentsEditEngines.delete(comment.value.uuid);

    this.getComments().removeAt(this.getComments().controls.indexOf(comment));
  }

  stopEditCurrentComment(): void {
    this.commentsEditEngines.get(this.currentComment?.value.uuid)?.stopEditing();

    if (this.currentComment) {
      this.currentComment = undefined;
    }
  }

  protected createCommentFormGroup(comment: ObeliskComment): FormGroup {
    return this.fb.group({
      uuid: [comment.uuid, [Validators.required]],
      coords: [comment.coords, [Validators.required]],
      text: [comment.text, [Validators.required]],
      imageUrl: [comment.imageUrl]
    });
  }

  protected addPathwayMapLayers(pathway: FormGroup) {
    const id = 'pathway-' + pathway.value.uuid;

    this.map.addSource(id, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: []
      }
    });

    this.map.addLayer({
      id: id + '-outline',
      type: 'line',
      source: id,
      layout: {},
      paint: {
        'line-color': '#efefef',
        'line-width': 4,
        'line-opacity': pathway.value.mappedToRoads ? 0.5 : 1,
        'line-dasharray': pathway.value.mappedToRoads ? [2, 2] : [1]
      }
    });

    this.map.addLayer({
      id,
      type: 'line',
      source: id,
      layout: {},
      paint: {
        'line-color': pathway.value.color,
        'line-width': 2,
        'line-opacity': pathway.value.mappedToRoads ? 0.5 : 1,
        'line-dasharray': pathway.value.mappedToRoads ? [2, 2] : [1]
      }
    });

    this.map.addSource(id + '-mapped', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: []
      }
    });

    this.map.addLayer({
      id: id + '-mapped-outline',
      type: 'line',
      source: id + '-mapped',
      layout: {},
      paint: {
        'line-color': '#efefef',
        'line-width': 4,
      }
    });

    this.map.addLayer({
      id: id + '-mapped',
      type: 'line',
      source: id + '-mapped',
      layout: {},
      paint: {
        'line-color': pathway.value.color,
        'line-width': 2,
      }
    });
  }

  protected pathwaysStrokeColorsUpdates(pathway: FormGroup): void {
    const id = 'pathway-' + pathway.value.uuid;

    this.pathwaysStrokeColors.get(pathway.value.uuid).subscribe((color) => {
      this.map.setPaintProperty(id, 'line-color', color);
      this.map.setPaintProperty(id + '-mapped', 'line-color', color);
    });
  }

  goToMyLocation(): void {
    if (this.userGeo) {
      this.map.setCenter([this.userGeo.lng, this.userGeo.lat]);
    } else {
      navigator.geolocation.getCurrentPosition((value) => {
        this.userGeo = {lng: value.coords.longitude, lat: value.coords.latitude};
        this.map.setCenter([this.userGeo.lng, this.userGeo.lat]);
      });
    }
  }

  toggleMagStyle(): void {
    if (this.map.getStyle().name === 'Mapbox Outdoors') {
      this.map.setStyle('mapbox://styles/mapbox/satellite-v9');
    } else {
      this.map.setStyle('mapbox://styles/mapbox/outdoors-v12');
    }

    this.map.once('styledata', () => {
      addLayersToMap(this.map);

      const pfg = this.getPathwaysAsFormGroups();

      if (pfg?.length) {
        this.getPathwaysAsFormGroups().forEach((pathway) => {
          this.addPathwayMapLayers(pathway);
          this.pathwaysStrokeColorsUpdates(pathway);
        });
      }

      this.map.once('styledata', () => {
        this.map.triggerRepaint();

        const pfg = this.getPathwaysAsFormGroups();

        if (pfg?.length) {
          this.getPathwaysAsFormGroups().forEach((pathway) => {
            this.pathwaysEditEngines.get(pathway.value.uuid).externalRedraw();
            if (pathway.value.mappedToRoads) {
              this.updateMappedPathwaysLayerData(pathway, pathway.value.mapMappedPath);
            }
          });
        }
      });
    });
  }

  protected storeLastLocation(coords: GeoCoords): void {
    localStorage.setItem('lastLocation', JSON.stringify(coords));
  }

  protected getLastLocation(): GeoCoords | null {
    const ll = localStorage.getItem('lastLocation');
    return ll ? JSON.parse(ll) : null;
  }


  abstract getPathways(): FormArray;
  abstract getPathwaysAsFormGroups(): FormGroup[];
  abstract getComments(): FormArray;
  abstract getCommentsAsFormGroups(): FormGroup[];

  protected createPathwayFormGroup(pathway: Pathway): FormGroup {
    return this.fb.group({
      uuid: [pathway.uuid, [Validators.required]],
      name: [pathway.name, [Validators.required]],
      color: [pathway.color, [Validators.required]],
      vertices: [pathway.vertices, [Validators.required, Validators.minLength(2)]],
      path: [pathway.path, [Validators.required]],
      mappedToRoads: [pathway.mappedToRoads],
      mapMappedPath: [pathway.mapMappedPath],
      mappedPathProfile: [pathway.mappedPathProfile || 'walking']
    });
  }

  addComment(): void {
    this.stopEditCurrentComment();

    const comment = this.createCommentFormGroup({
      uuid: uuidv4(),
      coords: undefined,
      text: ''
    });

    const cE = this.initComment(comment);

    this.currentComment = comment;
    this.getComments().push(comment);
  }

  hideComments(): void {
    this.commentsShown = false;
    this.commentsEditEngines.forEach((value) => value.hidePopup());
  }

  showComments(): void {
    this.commentsShown = true;
    this.commentsEditEngines.forEach((value) => value.showPopup());
  }

  initComment(comment: FormGroup): CommentsEditEngine {
    const cE = new CommentsEditEngine(this.map, comment.value.uuid, comment.value.coords, comment.value.text, comment.value.imageUrl);

    cE.onStartEdit$().subscribe(() => {
      this.commentsEditEngines.forEach((value) => {
        if (value.getData().uuid !== cE.getData().uuid) {
          value.stopEditing();
        }
      });

      this.currentComment = comment;
    });

    this.commentsEditEngines.set(comment.value.uuid, cE);

    return cE;
  }

  togglePathwayForMerge(pathway: FormGroup): void {
    const selected = this.selectedPathwaysForMerge();
    const index = selected.findIndex(p => p.value.uuid === pathway.value.uuid);

    if (index > -1) {
      this.selectedPathwaysForMerge.set(selected.filter((_, i) => i !== index));
    } else {
      this.selectedPathwaysForMerge.set([...selected, pathway]);
    }
  }

  isPathwaySelectedForMerge(pathway: FormGroup): boolean {
    return this.selectedPathwaysForMerge().some(p => p.value.uuid === pathway.value.uuid);
  }

  openMergeModal(): void {
    if (this.selectedPathwaysForMerge().length < 2) {
      this.snackBar.open(
        this.transloco.translate('selectAtLeastTwoPathways'),
        this.transloco.translate('close'),
        {
          verticalPosition: 'bottom',
          duration: 3000
        }
      );
      return;
    }
    this.showMergeModal.set(true);
  }

  closeMergeModal(): void {
    this.showMergeModal.set(false);
  }

  mergePathways(): void {
    const selected = this.selectedPathwaysForMerge();
    if (selected.length < 2) return;

    // Combine all vertices from all pathways
    const allVertices: { lat: number; lng: number }[] = [];
    selected.forEach(pathway => {
      const vertices = pathway.value.vertices || [];
      allVertices.push(...vertices);
    });

    if (allVertices.length < 2) {
      this.snackBar.open(
        this.transloco.translate('notEnoughVerticesForMerge'),
        this.transloco.translate('close'),
        {
          verticalPosition: 'bottom',
          duration: 3000
        }
      );
      return;
    }

    // Generate path (QGraph) from vertices
    const mergedPath = coordsToQGraph(allVertices);

    // Create a new pathway with combined vertices
    const newPathway = this.createPathwayFormGroup({
      uuid: uuidv4(),
      name: this.transloco.translate('mergedPathway'),
      color: selected[0].value.color,
      vertices: allVertices,
      path: mergedPath,
      mappedPathProfile: selected[0].value.mappedPathProfile || 'walking',
      mappedToRoads: false
    });

    // Remove old pathways
    selected.forEach(pathway => {
      this.removePathway(pathway);
    });

    // Add new merged pathway
    this.getPathways().push(newPathway);
    this.initPathway(newPathway);

    // Clear selection
    this.selectedPathwaysForMerge.set([]);
    this.closeMergeModal();

    this.snackBar.open(
      this.transloco.translate('pathwaysMerged'),
      this.transloco.translate('close'),
      {
        verticalPosition: 'bottom',
        duration: 3000
      }
    );
  }

  openSplitModal(pathway: FormGroup): void {
    const vertices = pathway.value.vertices || [];
    if (vertices.length < 3) {
      this.snackBar.open(
        this.transloco.translate('pathwayTooShortForSplit'),
        this.transloco.translate('close'),
        {
          verticalPosition: 'bottom',
          duration: 3000
        }
      );
      return;
    }

    this.pathwayForOperation.set(pathway);
    this.splitVertexIndex.set(Math.floor(vertices.length / 2));
    this.showSplitModal.set(true);
    this.updateSplitRunningMarker();
  }

  closeSplitModal(): void {
    this.showSplitModal.set(false);
    this.pathwayForOperation.set(null);
    this.removeSplitRunningMarker();
  }

  // Helper method to create running marker element
  private createRunningMarkerElement(label?: string): HTMLDivElement {
    const el = document.createElement('div');
    el.style.width = '24px';
    el.style.height = '24px';
    el.style.backgroundColor = '#ff0000';
    el.style.border = '3px solid white';
    el.style.borderRadius = '50%';
    el.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';

    if (label) {
      el.innerHTML = `<span style="color: white; font-size: 12px; font-weight: bold; display: flex; align-items: center; justify-content: center; height: 100%;">${label}</span>`;
    }

    return el;
  }

  updateSplitRunningMarker(): void {
    const pathway = this.pathwayForOperation();
    if (!pathway || !this.map || !this.mapLoaded) return;

    const vertices = pathway.value.vertices || [];
    const index = this.splitVertexIndex();

    if (index < 0 || index >= vertices.length) return;

    this.removeSplitRunningMarker();

    const vertex = vertices[index];

    // Validate coordinates
    if (!vertex || typeof vertex.lat !== 'number' || typeof vertex.lng !== 'number') {
      console.warn('Invalid vertex coordinates:', vertex);
      return;
    }

    const el = this.createRunningMarkerElement();

    this.splitRunningMarker = new mapboxgl.Marker(el)
      .setLngLat([vertex.lng, vertex.lat])
      .addTo(this.map);
  }

  removeSplitRunningMarker(): void {
    if (this.splitRunningMarker) {
      this.splitRunningMarker.remove();
      this.splitRunningMarker = undefined;
    }
  }

  onSplitVertexChange(index: number): void {
    this.splitVertexIndex.set(index);
    this.updateSplitRunningMarker();
  }

  splitPathway(): void {
    const pathway = this.pathwayForOperation();
    if (!pathway) return;

    const vertices = pathway.value.vertices || [];
    const splitIndex = this.splitVertexIndex();

    if (splitIndex <= 0 || splitIndex >= vertices.length - 1) {
      this.snackBar.open(
        this.transloco.translate('invalidSplitIndex'),
        this.transloco.translate('close'),
        {
          verticalPosition: 'bottom',
          duration: 3000
        }
      );
      return;
    }

    // Create two new pathways
    const vertices1 = vertices.slice(0, splitIndex + 1);
    const vertices2 = vertices.slice(splitIndex);

    // Generate path (QGraph) from vertices
    const path1 = coordsToQGraph(vertices1);
    const path2 = coordsToQGraph(vertices2);

    const pathway1 = this.createPathwayFormGroup({
      uuid: uuidv4(),
      name: pathway.value.name + ' (1)',
      color: pathway.value.color,
      vertices: vertices1,
      path: path1,
      mappedPathProfile: pathway.value.mappedPathProfile || 'walking',
      mappedToRoads: false
    });

    const pathway2 = this.createPathwayFormGroup({
      uuid: uuidv4(),
      name: pathway.value.name + ' (2)',
      color: pathway.value.color,
      vertices: vertices2,
      path: path2,
      mappedPathProfile: pathway.value.mappedPathProfile || 'walking',
      mappedToRoads: false
    });

    // Remove old pathway
    this.removePathway(pathway);

    // Add new pathways
    this.getPathways().push(pathway1);
    this.getPathways().push(pathway2);
    this.initPathway(pathway1);
    this.initPathway(pathway2);

    this.closeSplitModal();

    this.snackBar.open(
      this.transloco.translate('pathwaySplit'),
      this.transloco.translate('close'),
      {
        verticalPosition: 'bottom',
        duration: 3000
      }
    );
  }

  openCutModal(pathway: FormGroup, mode: 'start' | 'end' | 'section'): void {
    const vertices = pathway.value.vertices || [];
    if (vertices.length < 2) {
      this.snackBar.open(
        this.transloco.translate('pathwayTooShortForCut'),
        this.transloco.translate('close'),
        {
          verticalPosition: 'bottom',
          duration: 3000
        }
      );
      return;
    }

    this.pathwayForOperation.set(pathway);
    this.cutMode.set(mode);
    this.showCutModal.set(true);

    if (mode === 'start' || mode === 'end') {
      this.cutStartVertexIndex.set(mode === 'start' ? 0 : vertices.length - 2);
      this.cutEndVertexIndex.set(mode === 'start' ? 1 : vertices.length - 1);
    } else {
      // Section mode - default to middle section
      const mid = Math.floor(vertices.length / 2);
      this.cutStartVertexIndex.set(Math.max(0, mid - 2));
      this.cutEndVertexIndex.set(Math.min(vertices.length - 1, mid + 2));
    }

    this.updateCutRunningMarkers();
  }

  closeCutModal(): void {
    this.showCutModal.set(false);
    this.pathwayForOperation.set(null);
    this.cutMode.set(null);
    this.removeCutRunningMarkers();
  }

  updateCutRunningMarkers(): void {
    const pathway = this.pathwayForOperation();
    if (!pathway || !this.map || !this.mapLoaded) return;

    const vertices = pathway.value.vertices || [];
    const startIndex = this.cutStartVertexIndex();
    const endIndex = this.cutEndVertexIndex();
    const mode = this.cutMode();

    this.removeCutRunningMarkers();

    if (mode === 'section') {
      // Show both start and end markers for section cut
      if (startIndex >= 0 && startIndex < vertices.length) {
        const startVertex = vertices[startIndex];
        if (startVertex && typeof startVertex.lat === 'number' && typeof startVertex.lng === 'number') {
          const startEl = this.createRunningMarkerElement('S');

          this.cutRunningMarkers.push(
            new mapboxgl.Marker(startEl)
              .setLngLat([startVertex.lng, startVertex.lat])
              .addTo(this.map)
          );
        }
      }

      if (endIndex >= 0 && endIndex < vertices.length) {
        const endVertex = vertices[endIndex];
        if (endVertex && typeof endVertex.lat === 'number' && typeof endVertex.lng === 'number') {
          const endEl = this.createRunningMarkerElement('E');

          this.cutRunningMarkers.push(
            new mapboxgl.Marker(endEl)
              .setLngLat([endVertex.lng, endVertex.lat])
              .addTo(this.map)
          );
        }
      }
    } else {
      // For start/end mode, show only one marker at the cut point
      const index = mode === 'start' ? startIndex : endIndex;
      if (index >= 0 && index < vertices.length) {
        const vertex = vertices[index];
        if (vertex && typeof vertex.lat === 'number' && typeof vertex.lng === 'number') {
          const el = this.createRunningMarkerElement();

          this.cutRunningMarkers.push(
            new mapboxgl.Marker(el)
              .setLngLat([vertex.lng, vertex.lat])
              .addTo(this.map)
          );
        }
      }
    }
  }

  removeCutRunningMarkers(): void {
    this.cutRunningMarkers.forEach(marker => marker.remove());
    this.cutRunningMarkers = [];
  }

  onCutStartVertexChange(index: number): void {
    const pathway = this.pathwayForOperation();
    if (!pathway) return;

    const vertices = pathway.value.vertices || [];
    const endIndex = this.cutEndVertexIndex();

    // Ensure start is before end
    if (index >= endIndex) {
      this.cutStartVertexIndex.set(Math.max(0, endIndex - 1));
    } else {
      this.cutStartVertexIndex.set(index);
    }

    this.updateCutRunningMarkers();
  }

  onCutEndVertexChange(index: number): void {
    const pathway = this.pathwayForOperation();
    if (!pathway) return;

    const vertices = pathway.value.vertices || [];
    const startIndex = this.cutStartVertexIndex();

    // Ensure end is after start
    if (index <= startIndex) {
      this.cutEndVertexIndex.set(Math.min(vertices.length - 1, startIndex + 1));
    } else {
      this.cutEndVertexIndex.set(index);
    }

    this.updateCutRunningMarkers();
  }

  onCutVertexChange(index: number): void {
    const mode = this.cutMode();
    if (mode === 'start') {
      this.cutStartVertexIndex.set(index);
    } else if (mode === 'end') {
      const pathway = this.pathwayForOperation();
      if (pathway) {
        const vertices = pathway.value.vertices || [];
        this.cutEndVertexIndex.set(vertices.length - 1 - index);
      }
    }
    this.updateCutRunningMarkers();
  }

  cutPathway(): void {
    const pathway = this.pathwayForOperation();
    const mode = this.cutMode();
    if (!pathway || !mode) return;

    const vertices = pathway.value.vertices || [];
    const startIndex = this.cutStartVertexIndex();
    const endIndex = this.cutEndVertexIndex();

    let newVertices: { lat: number; lng: number }[];

    if (mode === 'start') {
      // Cut from start - keep vertices from cutStartVertexIndex onwards
      newVertices = vertices.slice(startIndex);
    } else if (mode === 'end') {
      // Cut from end - keep vertices up to and including cutEndVertexIndex
      newVertices = vertices.slice(0, endIndex + 1);
    } else {
      // Cut section - remove vertices between start and end, connect with straight line
      const before = vertices.slice(0, startIndex + 1);
      const after = vertices.slice(endIndex);
      newVertices = [...before, ...after];
    }

    if (newVertices.length < 2) {
      this.snackBar.open(
        this.transloco.translate('cutResultsTooFewVertices'),
        this.transloco.translate('close'),
        {
          verticalPosition: 'bottom',
          duration: 3000
        }
      );
      return;
    }

    // Update pathway with new vertices
    this.updatePathwayVertices(pathway, newVertices);

    this.closeCutModal();

    this.snackBar.open(
      this.transloco.translate('pathwayCut'),
      this.transloco.translate('close'),
      {
        verticalPosition: 'bottom',
        duration: 3000
      }
    );
  }

  simplifyPathway(pathway: FormGroup): void {
    const vertices = pathway.value.vertices || [];

    if (vertices.length < 3) {
      this.snackBar.open(
        this.transloco.translate('pathwayTooShortForSimplify'),
        this.transloco.translate('close'),
        {
          verticalPosition: 'bottom',
          duration: 3000
        }
      );
      return;
    }

    // Show confirmation dialog
    const confirmed = window.confirm(
      this.transloco.translate('simplifyPathwayConfirm')
    );

    if (!confirmed) {
      return;
    }

    // Convert path to GeoJSON LineString
    const coordinates = vertices.map((v: any) => [v.lng, v.lat]);
    const lineString: GeoJSON.Feature<GeoJSON.LineString> = {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'LineString',
        coordinates: coordinates
      }
    };

    // Calculate tolerance based on path length to achieve ~30% reduction
    const totalDistance = turf.length(lineString, { units: 'kilometers' });
    const tolerance = totalDistance * 0.000005; // Adjust this multiplier to control simplification

    // Simplify the line
    const simplified = turf.simplify(lineString, {
      tolerance: tolerance,
      highQuality: true
    });

    // Convert back to vertices
    const simplifiedCoords = simplified.geometry.coordinates;

    if (simplifiedCoords.length < 2) {
      this.snackBar.open(
        this.transloco.translate('simplificationResultsTooFewVertices'),
        this.transloco.translate('close'),
        {
          verticalPosition: 'bottom',
          duration: 3000
        }
      );
      return;
    }

    // Create new vertices array
    const newVertices = simplifiedCoords.map((coord: number[]) => ({
      lat: coord[1],
      lng: coord[0]
    }));

    // Update pathway with new vertices
    this.updatePathwayVertices(pathway, newVertices);

    this.snackBar.open(
      this.transloco.translate('pathwaySimplified', {
        before: vertices.length,
        after: newVertices.length
      }),
      this.transloco.translate('close'),
      {
        verticalPosition: 'bottom',
        duration: 3000
      }
    );
  }

  /**
   * Helper method to update pathway with new vertices and refresh the engine
   * Used by cut, simplify, and other pathway modification operations
   */
  private updatePathwayVertices(pathway: FormGroup, newVertices: { lat: number; lng: number }[]): void {
    // Generate new path from vertices
    const newPath = coordsToQGraph(newVertices);

    // Update pathway form values
    pathway.patchValue({
      vertices: newVertices,
      path: newPath,
      mappedToRoads: false
    });

    // Update the existing engine instead of reinitializing
    const engine = this.pathwaysEditEngines.get(pathway.value.uuid);
    if (engine) {
      // Update the zone data
      const [updatedZone, updatedOrderedVertices] = engine.toFunctionalZone(newPath);
      engine.zone = updatedZone;
      engine.orderedVertices = updatedOrderedVertices;

      // Redraw the pathway on the map
      engine.updateZoneOutlineOnMap(true);

      // Ensure pathway is not in edit mode
      engine.editing = false;
      engine.hideMarkers();
    }
  }

  protected loadMapMatching(pathway: FormGroup) {
    this.mapboxSpecificService.mapMatching(this.pathwaysEditEngines.get(pathway.value.uuid).orderedVertices.map((v) => v.coords), pathway.value.mappedPathProfile)
      .subscribe((value) => {
        if (!value.matchings?.length) {
          // pathway.patchValue({
          //   mappedToRoads: false
          // });
          this.snackBar.open(
            this.transloco.translate('canNotMapPathway'),
            this.transloco.translate('close'),
            {
              verticalPosition: 'bottom',
              duration: 5000,
              panelClass: 'error-snackbar'
            });

          const line = zoneToFeatureLineString(pathway.value.path).geometry as GeoJSON.LineString;

          pathway.patchValue({
            mapMappedPath: line
          });

          this.updateMappedPathwaysLayerData(pathway, line);
        } else {
          this.updateMappedPathwaysLayerData(pathway, value.matchings[0].geometry);
          pathway.patchValue({
            mapMappedPath: value.matchings[0].geometry
          })
        }
      });
  }

  protected updatePathLayers(pathway: FormGroup) {
    this.map.setPaintProperty('pathway-' + pathway.value.uuid, 'line-opacity', pathway.value.mappedToRoads ? 0.5 : 1);
    this.map.setPaintProperty('pathway-' + pathway.value.uuid, 'line-dasharray', pathway.value.mappedToRoads ? [2, 2] : [1]);
    this.map.setPaintProperty('pathway-' + pathway.value.uuid + '-outline', 'line-opacity', pathway.value.mappedToRoads ? 0.5 : 1);
    this.map.setPaintProperty('pathway-' + pathway.value.uuid + '-outline', 'line-dasharray', pathway.value.mappedToRoads ? [2, 2] : [1]);
  }

  protected updateMappedPathwaysLayerData(pathway: FormGroup, mapMappedLineString?: GeoJSON.LineString) {
    (this.map.getSource('pathway-' + pathway.value.uuid + '-mapped') as mapboxgl.GeoJSONSource).setData({
      type: 'FeatureCollection',
      features: mapMappedLineString ? [{
        type: 'Feature',
        geometry: mapMappedLineString,
        properties: {}
      }] : []
    });
  }

  protected drawCircle(zoneData: QGraphVertexWithCircle): void {
    const circle = turf.circle([zoneData.data.circleCenter.lng, zoneData.data.circleCenter.lat],
      zoneData.data.radius, {steps: 60, units: 'meters'});

    (this.map.getSource('task-zone') as mapboxgl.GeoJSONSource).setData(circle);
    (this.map.getSource('task-zone-outline') as mapboxgl.GeoJSONSource).setData(circle);
  }

  // Calculate distance of a pathway in kilometers
  calculatePathwayDistance(vertices: { lat: number; lng: number }[]): number {
    if (!vertices || vertices.length < 2) return 0;

    let totalDistance = 0;
    for (let i = 0; i < vertices.length - 1; i++) {
      const from = vertices[i];
      const to = vertices[i + 1];

      // Haversine formula to calculate distance between two coordinates
      const R = 6371; // Earth's radius in km
      const dLat = this.toRad(to.lat - from.lat);
      const dLng = this.toRad(to.lng - from.lng);

      const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(this.toRad(from.lat)) * Math.cos(this.toRad(to.lat)) *
        Math.sin(dLng / 2) * Math.sin(dLng / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const distance = R * c;

      totalDistance += distance;
    }

    return totalDistance;
  }

  protected toRad(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  // Get distance from start to a specific vertex index
  getDistanceToVertex(vertices: { lat: number; lng: number }[], vertexIndex: number): number {
    if (!vertices || vertexIndex < 0 || vertexIndex >= vertices.length) return 0;

    return this.calculatePathwayDistance(vertices.slice(0, vertexIndex + 1));
  }

  protected destroy() {
    this.removeSplitRunningMarker();
    this.removeCutRunningMarkers();

    this.pathwaysStrokeColors.forEach((value) => {
      value.complete();
    });
  }
}
